import { makeAutoObservable } from 'mobx';
import { ChipId, ChipInstanceId } from '@/types/ids';
import { Wap } from '@/utils/Wap/Wap';
import Context from '@/context';
import type { IChipInstanceModel, IChipModel, IProjectModel } from '@/interfaces/models';
import type { ProjectDto } from '@/schemas/chip';
import { nodeSnapshotWallAt, withNodeSnapshotTime } from '@/models/logic/SavedNodeState';
import {
  ProjectSnapshotBuilder,
  type CapturedProjectSnapshot,
} from '@/services/projectSnapshot/ProjectSnapshot';

export class ProjectModel implements IProjectModel {
  #nativeSnapshotModel = undefined;
  /** Brand check rejects opaque proxies before inspecting any snapshot hooks. */
  static isNativeSnapshotModel(value: unknown): value is ProjectModel {
    return (
      !!value &&
      typeof value === 'object' &&
      #nativeSnapshotModel in value &&
      Object.getPrototypeOf(value) === ProjectModel.prototype
    );
  }

  static #native_captureSnapshot = ProjectModel.prototype.captureSnapshot;
  chipInstanceMap: Wap<ChipInstanceId, IChipInstanceModel> = new Wap();

  constructor(
    private context: Context,
    public name: string,
  ) {
    makeAutoObservable(
      this,
      {
        dto: false,
        dtoSteps: false,
        captureSnapshot: false,
        trackSnapshotDependencies: false,
        observeSnapshotInputs: false,
      } as any,
      { autoBind: true },
    );
  }

  get mainChip(): IChipModel {
    const mainChip = this.context.chipStore.getChip(this.context.mainChip.id);

    if (!mainChip) {
      throw new Error(`Main chip not found: ${this.context.mainChip.id}`);
    }

    return mainChip;
  }

  get dto(): ProjectDto {
    return withNodeSnapshotTime(nodeSnapshotWallAt(), () => {
      const chips = this.context.chipStore.chips.map((chip) => chip.dto);
      return {
        ...(this.context.startPaused ? { startPaused: true as const } : {}),
        name: this.name,
        mainChipId: this.context.mainChip.id,
        chips,
      };
    });
  }

  *dtoSteps(): Generator<void, ProjectDto> {
    const chips: ProjectDto['chips'] = [];
    for (const chip of this.context.chipStore.chips) {
      chips.push(chip.dtoSteps ? yield* chip.dtoSteps() : chip.dto);
      yield;
    }
    return {
      ...(this.context.startPaused ? { startPaused: true as const } : {}),
      name: this.name,
      mainChipId: this.context.mainChip.id,
      chips,
    };
  }

  /** One synchronous epoch. No graph-sized node DTO arrays or yielded live views. */
  captureSnapshot(extraChips: readonly IChipModel[] = []): CapturedProjectSnapshot {
    return withNodeSnapshotTime(nodeSnapshotWallAt(), () => {
      const builder = new ProjectSnapshotBuilder();
      try {
        const chips = [...this.context.chipStore.chips, ...extraChips].map((chip) => {
          if (!chip.captureSnapshot)
            throw new Error('This chip does not support current snapshot capture.');
          return chip.captureSnapshot(builder);
        });
        return builder.finish({
          ...(this.context.startPaused ? { startPaused: true as const } : {}),
          name: this.name,
          mainChipId: this.context.mainChip.id,
          chips,
        });
      } catch (error) {
        builder.abandon();
        throw error;
      }
    });
  }
  observeSnapshotInputs(): boolean {
    if (this.captureSnapshot !== ProjectModel.#native_captureSnapshot) return false;
    void this.name;
    void this.context.mainChip.id;
    for (const chip of this.context.chipStore.chips)
      if (chip.observeSnapshotInputs?.() !== true) return false;
    return true;
  }

  trackSnapshotDependencies(): void {
    void this.name;
    void this.context.mainChip.id;
    for (const chip of this.context.chipStore.chips) {
      try {
        chip.trackSnapshotDependencies?.();
      } catch {
        /* A broken extension chip must not hide edits in later chips. */
      }
    }
  }
}
